/**
 * Contrato del ACTA de recepción (DEVS-4000).
 *
 * El acta es la cabecera local (`anmat_task`) de un remito o factura que un
 * emisor informó a ANMAT hacia nuestro GLN. Nace al abrirla con el snapshot
 * completo de series de ANMAT; el operador marca las que recibió (escaneo o
 * tilde), confirma (job asíncrono en riga, `tipo = 'ingreso'`) y cierra
 * dejando el resto como no recibido con motivo.
 */

import { z } from 'zod';
import { ESTADOS_ACTA_RECEPCION, ESTADOS_SERIE_ACTA, MOTIVOS_NO_RECIBIDA } from '@/domain/types';

export const EstadoActaSchema = z.enum(ESTADOS_ACTA_RECEPCION);
export const EstadoSerieActaSchema = z.enum(ESTADOS_SERIE_ACTA);
export const MotivoNoRecibidaSchema = z.enum(MOTIVOS_NO_RECIBIDA);
export const StockDestinoSchema = z.enum(['a', 'na']);

/** De dónde viene la unidad: si ya estuvo en stock (lo normal: devolución) y de qué despacho salió. */
export const OrigenSerieSchema = z.object({
  tipo: z.enum(['reingreso', 'nuevo']),
  enStock: z.boolean(),
  egresada: z.boolean().optional(),
  fEgreso: z.string().nullable().optional(),
  despacho: z
    .object({
      referencia: z.string().nullable(),
      numeroRemito: z.string().nullable(),
    })
    .nullable()
    .optional(),
});

export const SerieActaSchema = z.object({
  id: z.number().int(),
  idTransaccion: z.string().nullable(),
  idTransaccionGlobal: z.string().nullable(),
  idEvento: z.string().nullable(),
  descripcionEvento: z.string().nullable(),
  /** YYYY-MM-DD */
  fechaEvento: z.string().nullable(),
  fechaTransaccion: z.string().nullable(),
  gtin: z.string().nullable(),
  nombre: z.string().nullable(),
  lote: z.string().nullable(),
  numeroSerial: z.string().nullable(),
  /** YYYY-MM-DD */
  vencimiento: z.string().nullable(),
  estado: EstadoSerieActaSchema,
  marcadaAt: z.string().nullable(),
  marcadaVia: z.enum(['escaneo', 'manual']).nullable(),
  marcadaUsuario: z.string().nullable(),
  noRecibidaMotivo: MotivoNoRecibidaSchema.nullable(),
  noRecibidaDetalle: z.string().nullable(),
  noRecibidaAt: z.string().nullable(),
  noRecibidaUsuario: z.string().nullable(),
  idTransacTraza: z.string().nullable(),
  errorMessage: z.string().nullable(),
  tracedAt: z.string().nullable(),
  /** Último estado visto en ANMAT: '1' informada, '2' anulada por el emisor, '3' confirmada. */
  anmatIdEstado: z.string().nullable(),
  anmatVerificadoAt: z.string().nullable(),
  origen: OrigenSerieSchema.optional(),
});

export const SobranteActaSchema = z.object({
  id: z.number().int(),
  gtin: z.string().nullable(),
  lote: z.string().nullable(),
  numeroSerial: z.string(),
  vencimiento: z.string().nullable(),
  lectura: z.string().nullable(),
  observacion: z.string().nullable(),
  usuario: z.string().nullable(),
  createdAt: z.string().nullable(),
  origen: OrigenSerieSchema.optional(),
});

export const TotalesActaSchema = z.object({
  pendiente: z.number().int(),
  marcada: z.number().int(),
  trazado: z.number().int(),
  error: z.number().int(),
  noRecibida: z.number().int(),
  anulada: z.number().int(),
  total: z.number().int(),
});

export const ActaRecepcionSchema = z.object({
  id: z.number().int(),
  numeroRemito: z.string().nullable(),
  numeroFactura: z.string().nullable(),
  glnOrigen: z.string().nullable(),
  glnDestino: z.string().nullable(),
  razonSocialOrigen: z.string().nullable(),
  razonSocialDestino: z.string().nullable(),
  cantSeries: z.number().int(),
  estado: EstadoActaSchema,
  stockDestino: StockDestinoSchema.nullable(),
  glnId: z.number().int(),
  userId: z.number().int().nullable(),
  tracedAt: z.string().nullable(),
  cerradoAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  totales: TotalesActaSchema,
  series: z.array(SerieActaSchema),
  sobrantes: z.array(SobranteActaSchema),
});

/** Lo que pasó al abrir: series nuevas, y qué dijo ANMAT de las que ya no lista. */
export const NovedadesActaSchema = z.object({
  seriesNuevas: z.number().int().optional(),
  anuladasPorEmisor: z.number().int().optional(),
  confirmadasFuera: z.number().int().optional(),
  actaAnulada: z.boolean().optional(),
});

export const AbrirActaResponseSchema = z.object({
  acta: ActaRecepcionSchema,
  novedades: NovedadesActaSchema,
});

export const ActaResponseSchema = z.object({ acta: ActaRecepcionSchema });

/** Candidatos cuando dos emisores comparten el número (409 ACTA_EMISOR_AMBIGUO → `details`). */
export const EmisorCandidatoSchema = z.object({
  glnOrigen: z.string().nullable(),
  razonSocialOrigen: z.string().nullable(),
  cantSeries: z.number().int(),
});

export const MarcaResponseSchema = z.object({
  resultado: z.enum(['marcada', 'sobrante']),
  serie: SerieActaSchema.nullable().optional(),
  sobrante: SobranteActaSchema.nullable().optional(),
  advertencias: z.array(z.string()),
});

export const TrazaResumenSchema = z.object({
  id: z.number().int(),
  jobId: z.number().int(),
  status: z.string(),
  totalItems: z.number().nullable(),
  doneItems: z.number().nullable(),
  errorItems: z.number().nullable(),
  errorMessage: z.string().nullable(),
  finishedAt: z.string().nullable(),
  lastStatusCheckAt: z.string().nullable(),
});

export const ConfirmarActaResponseSchema = z.object({
  trazaJobId: z.number().int(),
  traza: TrazaResumenSchema.nullable(),
  seriesEnviadas: z.number().int(),
  acta: ActaRecepcionSchema,
});

export const CerrarActaResponseSchema = z.object({
  estado: EstadoActaSchema,
  noRecibidas: z.number().int(),
  totales: TotalesActaSchema,
  acta: ActaRecepcionSchema,
});

export const RecuperarActaResponseSchema = z.object({
  confirmadas: z.number().int(),
  anuladas: z.number().int(),
  sinNovedad: z.number().int(),
  stock: z.record(z.unknown()).nullable(),
  estado: EstadoActaSchema,
  acta: ActaRecepcionSchema,
});

/** Filas del export para Plataforma (una por serie) + sobrantes aparte. */
export const ExportSerieSchema = z.object({
  actaId: z.number().int(),
  numeroRemito: z.string().nullable(),
  numeroFactura: z.string().nullable(),
  glnOrigen: z.string().nullable(),
  razonSocialOrigen: z.string().nullable(),
  glnDestino: z.string().nullable(),
  razonSocialDestino: z.string().nullable(),
  actaEstado: z.string(),
  stockDestino: z.string().nullable(),
  cerradoAt: z.string().nullable(),
  cerradoPor: z.string().nullable(),
  detailId: z.number().int(),
  idTransaccion: z.string().nullable(),
  idTransaccionGlobal: z.string().nullable(),
  idTransacTraza: z.string().nullable(),
  idEvento: z.string().nullable(),
  descripcionEvento: z.string().nullable(),
  fechaTransaccion: z.string().nullable(),
  gtin: z.string().nullable(),
  nombre: z.string().nullable(),
  lote: z.string().nullable(),
  numeroSerial: z.string().nullable(),
  vencimiento: z.string().nullable(),
  estado: z.string(),
  tracedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  marcadaVia: z.string().nullable(),
  marcadaAt: z.string().nullable(),
  marcadaPor: z.string().nullable(),
  noRecibidaMotivo: z.string().nullable(),
  noRecibidaDetalle: z.string().nullable(),
  noRecibidaAt: z.string().nullable(),
  noRecibidaPor: z.string().nullable(),
  anmatIdEstado: z.string().nullable(),
});

export const ExportSobranteSchema = z.object({
  actaId: z.number().int(),
  numeroRemito: z.string().nullable(),
  numeroFactura: z.string().nullable(),
  glnOrigen: z.string().nullable(),
  razonSocialOrigen: z.string().nullable(),
  gtin: z.string().nullable(),
  lote: z.string().nullable(),
  numeroSerial: z.string(),
  vencimiento: z.string().nullable(),
  lectura: z.string().nullable(),
  observacion: z.string().nullable(),
  registradoAt: z.string().nullable(),
  registradoPor: z.string().nullable(),
});

export const ExportRecepcionesResponseSchema = z.object({
  series: z.array(ExportSerieSchema),
  sobrantes: z.array(ExportSobranteSchema),
});

export type EstadoActa = z.infer<typeof EstadoActaSchema>;
export type EstadoSerieActa = z.infer<typeof EstadoSerieActaSchema>;
export type MotivoNoRecibida = z.infer<typeof MotivoNoRecibidaSchema>;
export type StockDestino = z.infer<typeof StockDestinoSchema>;
export type OrigenSerie = z.infer<typeof OrigenSerieSchema>;
export type SerieActa = z.infer<typeof SerieActaSchema>;
export type SobranteActa = z.infer<typeof SobranteActaSchema>;
export type TotalesActa = z.infer<typeof TotalesActaSchema>;
export type ActaRecepcion = z.infer<typeof ActaRecepcionSchema>;
export type NovedadesActa = z.infer<typeof NovedadesActaSchema>;
export type AbrirActaResponse = z.infer<typeof AbrirActaResponseSchema>;
export type EmisorCandidato = z.infer<typeof EmisorCandidatoSchema>;
export type MarcaResponse = z.infer<typeof MarcaResponseSchema>;
export type ConfirmarActaResponse = z.infer<typeof ConfirmarActaResponseSchema>;
export type CerrarActaResponse = z.infer<typeof CerrarActaResponseSchema>;
export type RecuperarActaResponse = z.infer<typeof RecuperarActaResponseSchema>;
export type ExportSerie = z.infer<typeof ExportSerieSchema>;
export type ExportSobrante = z.infer<typeof ExportSobranteSchema>;
export type ExportRecepciones = z.infer<typeof ExportRecepcionesResponseSchema>;
